import { scheduleToDays, toDisplayDayIndex } from '@/domain/templates/day-of-week';
import { fixedDayCount, type DayCountResolver } from '@/domain/templates/staffing';
import type { ShiftSet } from '@/types/template';

/**
 * A grouped "deck" in the stacked preview: all shifts of the same type + time on
 * one day, collapsed to a single card showing the total head count.
 */
export interface ShiftStack {
  key: string;
  typeName: string;
  time: string;
  /** Total required heads across the grouped shifts (the `×N` badge). */
  count: number;
  draft: boolean;
}

/**
 * Groups shifts (and an optional draft) into per-display-day stacks: one stack
 * per `shiftType.name` + time on each ISO day the shift runs, with `count` the
 * summed head count. The draft forms its own stack so it stays a visible ghost.
 * `resolveCount` defaults to the fixed staffing count; pass a scaling resolver to
 * vary the head count per day.
 */
export function buildShiftStacks(
  shifts: ShiftSet[],
  draftShift: ShiftSet | null,
  resolveCount: DayCountResolver = fixedDayCount,
): ShiftStack[][] {
  const columns: ShiftStack[][] = Array.from({ length: 7 }, () => []);

  const add = (shift: ShiftSet, draft: boolean) => {
    const typeName = shift.shiftType.name;
    const time = `${shift.startTime}–${shift.endTime}`;
    // Draft stacks never merge with saved ones, so they remain a distinct ghost.
    const groupKey = `${draft ? 'draft-' : ''}${typeName} ${time}`;

    for (const dayOfWeek of scheduleToDays(shift.schedule)) {
      const heads = resolveCount(shift, dayOfWeek);
      const column = columns[toDisplayDayIndex(dayOfWeek)];
      const existing = column.find((stack) => stack.key === groupKey);
      if (existing) {
        existing.count += heads;
      } else {
        column.push({ key: groupKey, typeName, time, count: heads, draft });
      }
    }
  };

  for (const shift of shifts) add(shift, false);
  if (draftShift) add(draftShift, true);

  return columns;
}

/**
 * Number of decorative layers shown behind a stack card for the isometric
 * "deck of cards" look — deeper for bigger stacks, capped at 3.
 */
export function deckDepth(count: number): number {
  if (count <= 1) return 0;
  if (count === 2) return 1;
  if (count <= 4) return 2;
  return 3;
}
